if (!customElements.get('nd-home-showcase')) {
  customElements.define(
    'nd-home-showcase',
    class NDHomeShowcase extends HTMLElement {
      constructor() {
        super();
        this.slides = Array.from(this.querySelectorAll('[data-showcase-slide]'));
        this.dots = Array.from(this.querySelectorAll('[data-showcase-dot]'));
        this.previousButton = this.querySelector('[data-showcase-previous]');
        this.nextButton = this.querySelector('[data-showcase-next]');
        this.currentIndex = Math.max(
          0,
          this.slides.findIndex((slide) => slide.getAttribute('aria-hidden') === 'false')
        );
        this.interval = Number(this.dataset.interval) || 2000;
        this.autoplay = this.dataset.autoplay === 'true';
        this.timer = null;
        this.pauseReasons = new Set();
        this.pointerId = null;
        this.pointerStartX = 0;
        this.pointerStartY = 0;
        this.pointerDragging = false;
        this.suppressNextClick = false;
        this.touchId = null;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchLastX = 0;
        this.touchDragging = false;

        if (this.slides.length < 2) return;

        this.previousButton?.addEventListener('click', () => this.show(this.currentIndex - 1, true));
        this.nextButton?.addEventListener('click', () => this.show(this.currentIndex + 1, true));
        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index, true)));

        if ('PointerEvent' in window) {
          this.addEventListener('pointerenter', (event) => {
            if (event.pointerType !== 'touch') this.pause('hover');
          });
          this.addEventListener('pointerleave', (event) => {
            if (event.pointerType !== 'touch') this.resume('hover');
          });
        } else {
          this.addEventListener('mouseenter', () => this.pause('hover'));
          this.addEventListener('mouseleave', () => this.resume('hover'));
        }
        this.addEventListener('pointerdown', (event) => this.onPointerDown(event));
        this.addEventListener('pointermove', (event) => this.onPointerMove(event));
        this.addEventListener('pointerup', (event) => this.onPointerEnd(event));
        this.addEventListener('pointercancel', (event) => this.onPointerEnd(event));
        this.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
        this.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false });
        this.addEventListener('touchend', (event) => this.onTouchEnd(event));
        this.addEventListener('touchcancel', (event) => this.onTouchEnd(event));
        this.addEventListener('dragstart', (event) => event.preventDefault());
        this.addEventListener('click', (event) => {
          if (!this.suppressNextClick) return;
          event.preventDefault();
          event.stopPropagation();
          this.suppressNextClick = false;
        }, true);
        this.show(this.currentIndex);
        this.start();
      }

      disconnectedCallback() {
        this.stop();
      }

      onPointerDown(event) {
        if (
          event.pointerType === 'touch' ||
          !event.isPrimary ||
          (event.pointerType === 'mouse' && event.button !== 0)
        ) return;
        this.pointerId = event.pointerId;
        this.pointerStartX = event.clientX;
        this.pointerStartY = event.clientY;
        this.pointerDragging = false;
        this.pause('pointer');
      }

      onPointerMove(event) {
        if (event.pointerId !== this.pointerId) return;
        const distanceX = event.clientX - this.pointerStartX;
        const distanceY = event.clientY - this.pointerStartY;
        if (!this.pointerDragging) {
          if (Math.abs(distanceX) < 10 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
          this.pointerDragging = true;
          this.classList.add('is-dragging');
          this.setPointerCapture?.(event.pointerId);
        }
        if (event.cancelable) event.preventDefault();
      }

      onPointerEnd(event) {
        if (event.pointerId !== this.pointerId) return;
        const distanceX = event.clientX - this.pointerStartX;
        const didDrag = this.pointerDragging;
        if (event.type === 'pointerup' && didDrag && Math.abs(distanceX) > 45) {
          this.show(this.currentIndex + (distanceX < 0 ? 1 : -1), true);
        }
        if (this.hasPointerCapture?.(event.pointerId)) this.releasePointerCapture(event.pointerId);
        this.pointerId = null;
        this.pointerDragging = false;
        this.classList.remove('is-dragging');
        this.suppressNextClick = event.type === 'pointerup' && didDrag;
        if (this.suppressNextClick) window.setTimeout(() => { this.suppressNextClick = false; }, 250);
        this.resume('pointer');
      }

      findTouch(touchList) {
        return Array.from(touchList).find((touch) => touch.identifier === this.touchId);
      }

      onTouchStart(event) {
        if (this.touchId !== null || event.touches.length === 0) return;
        const touch = event.changedTouches[0] || event.touches[0];
        this.touchId = touch.identifier;
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchLastX = touch.clientX;
        this.touchDragging = false;
        this.pause('touch');
      }

      onTouchMove(event) {
        if (this.touchId === null) return;
        const touch = this.findTouch(event.touches);
        if (!touch) return;
        this.touchLastX = touch.clientX;
        const distanceX = touch.clientX - this.touchStartX;
        const distanceY = touch.clientY - this.touchStartY;
        if (!this.touchDragging) {
          if (Math.abs(distanceX) < 10 || Math.abs(distanceX) <= Math.abs(distanceY)) return;
          this.touchDragging = true;
          this.classList.add('is-dragging');
        }
        if (event.cancelable) event.preventDefault();
      }

      onTouchEnd(event) {
        if (this.touchId === null) return;
        const touch = this.findTouch(event.changedTouches);
        if (!touch && event.type !== 'touchcancel') return;
        const distanceX = (touch?.clientX ?? this.touchLastX) - this.touchStartX;
        const didDrag = this.touchDragging;
        if (event.type === 'touchend' && didDrag && Math.abs(distanceX) > 45) {
          this.show(this.currentIndex + (distanceX < 0 ? 1 : -1), true);
        }
        this.touchId = null;
        this.touchDragging = false;
        this.classList.remove('is-dragging');
        this.suppressNextClick = event.type === 'touchend' && didDrag;
        if (this.suppressNextClick) window.setTimeout(() => { this.suppressNextClick = false; }, 350);
        this.resume('touch');
      }

      show(nextIndex, userInitiated = false) {
        const count = this.slides.length;
        this.currentIndex = (nextIndex + count) % count;

        this.slides.forEach((slide, index) => {
          const active = index === this.currentIndex;
          slide.setAttribute('aria-hidden', String(!active));
          if ('inert' in slide) slide.inert = !active;
          slide.querySelectorAll('a, button').forEach((element) => {
            element.tabIndex = active ? 0 : -1;
          });
        });

        this.dots.forEach((dot, index) => {
          dot.setAttribute('aria-current', String(index === this.currentIndex));
        });

        if (userInitiated) {
          this.stop();
          this.start();
        }
      }

      start() {
        this.stop();
        if (window.matchMedia('(hover: hover)').matches && this.matches(':hover')) {
          this.pauseReasons.add('hover');
        }
        if (
          !this.autoplay ||
          this.slides.length < 2 ||
          this.pauseReasons.size > 0 ||
          window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ) return;
        this.timer = window.setInterval(() => this.show(this.currentIndex + 1), this.interval);
      }

      pause(reason) {
        this.pauseReasons.add(reason);
        this.stop();
      }

      resume(reason) {
        this.pauseReasons.delete(reason);
        if (this.pauseReasons.size === 0) this.start();
      }

      stop() {
        if (this.timer !== null) window.clearInterval(this.timer);
        this.timer = null;
      }
    }
  );
}
